import React, { useState, useEffect, useRef } from 'react';
import { mobileObjectDetailService } from '../services/mobileObjectDetailService.js';
import { useApiError } from '../../shared/hooks/useApiError.js';
import { MessageDialog } from '../../shared/ui/index.js';
import CanvasAnnotator from '../../lib/canvas-annotator.es.js';

/**
 * ImageHandwriteEditModalコンポーネントのスタイル定義
 */
const styles = {
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.8)',
    zIndex: 3000,
    display: 'flex',
    flexDirection: 'column'
  },
  modalContent: {
    background: 'white',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #ddd',
    backgroundColor: '#f9f9f9'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333'
  },
  closeButton: {
    background: '#666',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    gap: 0
  },
  imageSelectionPanel: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
    backgroundColor: '#f5f5f5',
    maxHeight: '150px',
    overflowY: 'auto'
  },
  imageSelectionTitle: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#333'
  },
  imageSelectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
    gap: '8px'
  },
  imageSelectionItem: {
    position: 'relative',
    cursor: 'pointer',
    border: '2px solid #ddd',
    borderRadius: '5px',
    overflow: 'hidden',
    backgroundColor: '#2B2222',
    pointerEvents: 'auto',
    zIndex: 1
  },
  imageSelectionItemSelected: {
    border: '2px solid #F93535'
  },
  imageSelectionThumbnail: {
    width: '100%',
    height: '70px',
    objectFit: 'contain',
    display: 'block',
    pointerEvents: 'none'
  },
  canvasContainer: {
    flex: '1 1 auto',
    minHeight: 0,
    position: 'relative',
    backgroundColor: '#2B2222',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0
  },
  canvasWrapper: {
    position: 'relative',
    maxWidth: '100%',
    maxHeight: '100%',
    flexShrink: 0
  },
  canvas: {
    display: 'block',
    border: '1px solid #ccc',
    background: '#fff'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    zIndex: 20
  },
  loadingText: {
    color: '#666',
    fontSize: '14px'
  },
  noImageMessage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 10
  },
  noImageText: {
    color: '#666',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: '12px 20px',
    borderRadius: '5px',
    fontSize: '14px'
  },
  controlsPanel: {
    padding: '10px',
    borderTop: '1px solid #ddd',
    backgroundColor: '#f9f9f9',
    overflowY: 'visible',
    flexShrink: 0,
    marginTop: 0
  },
  controlsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '8px',
    alignItems: 'flex-end'
  },
  controlGroup: {
    minWidth: '90px',
    flex: '1 1 auto'
  },
  controlLabel: {
    fontSize: '11px',
    fontWeight: 'bold',
    marginBottom: '3px',
    color: '#333'
  },
  toolButtons: {
    display: 'flex',
    gap: '8px'
  },
  toolButton: {
    flex: 1,
    padding: '6px 10px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    backgroundColor: '#f0f0f0',
    color: '#333',
    cursor: 'pointer',
    fontSize: '11px',
    textAlign: 'center',
    height: '32px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  toolButtonActive: {
    backgroundColor: '#F93535',
    color: 'white',
    borderColor: '#F93535'
  },
  colorPicker: {
    width: '48px',
    height: '36px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  slider: {
    width: '100%'
  },
  select: {
    width: '100%',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '12px',
    backgroundColor: 'white'
  },
  actionButtons: {
    display: 'flex',
    gap: '4px'
  },
  actionButton: {
    flex: 1,
    padding: '6px 10px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    backgroundColor: '#f0f0f0',
    color: '#333',
    cursor: 'pointer',
    fontSize: '11px',
    textAlign: 'center'
  },
  clearButton: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    borderColor: '#ef5350'
  },
  saveButton: {
    width: '100%',
    padding: '10px',
    background: '#F93535',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    marginTop: '6px'
  },
  saveButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed'
  }
};

/**
 * ImageAnnotationModalコンポーネント
 * 画像に注釈を追加するためのモーダル
 * 
 * @param {boolean} isOpen - モーダルの表示状態
 * @param {Function} onClose - モーダルを閉じるコールバック
 * @param {Array} imageData - 画像データ配列
 * @param {string} objectId - オブジェクトID
 * @param {Function} onAnnotationComplete - 注釈完了時のコールバック
 */
const ImageHandwriteEditModal = ({ isOpen, onClose, imageData, objectId, onHandwriteEditComplete }) => {
  const {
    showErrorDialog,
    errorMessage,
    messageType,
    handleApiError,
    closeErrorDialog,
    showSuccessMessage
  } = useApiError();

  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveOption, setSaveOption] = useState('1'); // 1: increment editId, 2: new imageId
  
  // Canvas annotator state
  const baseCanvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const annotRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(4);
  const [eraserSize, setEraserSize] = useState(20);
  const [fit, setFit] = useState('stretch'); // Changed to 'stretch' since canvas will match image size
  // 画像未選択時も選択後と同じ available 領域に合わせる（上部の画像選択パネルと重ならないようにする）
  const getAvailableCanvasSize = (imageDataParam) => {
    const headerHeight = 60;
    const imageSelectionHeight = (imageDataParam && imageDataParam.length > 0) ? 150 : 0;
    const controlsHeight = 200;
    const padding = 20;
    const availableWidth = Math.max(300, window.innerWidth - padding * 2);
    const availableHeight = Math.max(250, window.innerHeight - headerHeight - imageSelectionHeight - controlsHeight - padding * 2);
    return { width: availableWidth, height: availableHeight };
  };

  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 250 }); // 初期値は小さい値、モーダル表示時に getAvailableCanvasSize で上書き
  const pendingImageUrlRef = useRef(null); // Store image URL to load after canvas resize
  const [imageReloadTrigger, setImageReloadTrigger] = useState(0); // Force image reload

  // モーダル表示時に編集エリアの初期サイズを「画像選択後」と同じ MAX に合わせる
  // 画像選択中は実行しない（imageData 更新でサイズを上書きすると背景キャンバスがクリアされ、手書きだけ残る問題を防ぐ）
  useEffect(() => {
    if (isOpen && !selectedImage) {
      setCanvasSize(getAvailableCanvasSize(imageData));
    }
  }, [isOpen, imageData, selectedImage]);

  
  useEffect(() => {
    if (!isOpen || selectedImage || !canvasContainerRef.current) return;

    const el = canvasContainerRef.current;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setCanvasSize({ width: Math.floor(width), height: Math.floor(height) });
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen, selectedImage]);

  const ensure = () => {
    if (!annotRef.current) throw new Error('Annotator not ready');
    return annotRef.current;
  };

  // Initialize canvas annotator when modal opens or canvas size changes
  useEffect(() => {
    if (!isOpen || !baseCanvasRef.current) return;

    const annot = new CanvasAnnotator();

    annot.Init({
      canvas: baseCanvasRef.current,
      width: canvasSize.width,
      height: canvasSize.height,
      fit: fit,
      tool: tool,
      eraserSize: eraserSize,
      devicePixelRatioAware: true,
      strokeStyle: color,
      lineWidth: lineWidth,
      lineCap: 'round',
    });

    annotRef.current = annot;

    // Load pending image after annotator is ready
    if (pendingImageUrlRef.current) {
      const url = pendingImageUrlRef.current;
      pendingImageUrlRef.current = null;
      annot.Load({ url: url })
        .catch(error => {
          console.error('Failed to load pending image:', error);
        });
    }

    return () => {
      const annot = annotRef.current;
      if (annot) {
        if (annot.fgCanvas && annot.fgCanvas.parentNode) {
          annot.fgCanvas.parentNode.removeChild(annot.fgCanvas);
        }
        annotRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, canvasSize.width, canvasSize.height]);

  // Update annotator settings when they change
  useEffect(() => {
    if (!annotRef.current) return;
    annotRef.current.SetFit(fit);
  }, [fit]);

  useEffect(() => {
    if (!annotRef.current) return;
    annotRef.current.SetTool(tool);
  }, [tool]);

  useEffect(() => {
    if (!annotRef.current) return;
    annotRef.current.SetStroke({ strokeStyle: color, lineWidth: lineWidth });
  }, [color, lineWidth]);

  useEffect(() => {
    if (!annotRef.current) return;
    annotRef.current.SetEraser({ size: eraserSize });
  }, [eraserSize]);

  // Load image when selected and resize canvas to match image dimensions
  useEffect(() => {
    if (!selectedImage || !objectId || !isOpen) return;

    const loadImage = async () => {
      setIsLoading(true);
      try {
        // Check if image has required properties
        // Use objectId from props if not in image object
        const imageObjectId = selectedImage.objectId || objectId;
        // objectImageId can be 0, so check for undefined/null/empty string specifically
        const hasObjectImageId = selectedImage.objectImageId !== undefined && 
                                 selectedImage.objectImageId !== null && 
                                 selectedImage.objectImageId !== '';
        if (!imageObjectId || !hasObjectImageId) {
          console.error('Invalid image data:', {
            imageObjectId,
            objectImageId: selectedImage.objectImageId,
            selectedImage
          });
          throw new Error('画像データが不正です');
        }

        // Get the max edit ID to load the latest version of the image for annotation
        // This ensures we're annotating the most recent version
        let objectImageEditId;
        try {
          const maxEditIdResult = await mobileObjectDetailService.getMaxImageEditId(
            imageObjectId,
            selectedImage.objectImageId
          );
          // Use the max edit ID (the latest version)
          objectImageEditId = maxEditIdResult.objectImageEditId;
        } catch (error) {
          console.warn('Failed to get max edit ID, using image objectImageEditId:', error);
          // Fallback to the edit ID from the image object if getting max fails
          // Use the actual value even if it's 0 (0 is a valid edit ID)
          objectImageEditId = (selectedImage.objectImageEditId !== undefined && 
                              selectedImage.objectImageEditId !== null) 
                              ? selectedImage.objectImageEditId 
                              : null;
        }

        const response = await mobileObjectDetailService.getImageBlobForEdit(
          imageObjectId,
          selectedImage.objectImageId,
          objectImageEditId,
          selectedImage.filenameExtension || 'jpg'
        );
        
        // Convert blob to URL and get image dimensions first
        const blobUrl = URL.createObjectURL(response.data);
        const img = new Image();
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            const { width: availableWidth, height: availableHeight } = getAvailableCanvasSize(imageData);

            // Calculate canvas size to match image, but fit within available viewport
            let canvasWidth = img.width;
            let canvasHeight = img.height;
            
            // Scale down to fit viewport while maintaining aspect ratio
            const scaleX = availableWidth / canvasWidth;
            const scaleY = availableHeight / canvasHeight;
            const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down
            
            if (scale < 1) {
              canvasWidth = Math.round(canvasWidth * scale);
              canvasHeight = Math.round(canvasHeight * scale);
            }
            
            // Store image URL to load after canvas resizes
            pendingImageUrlRef.current = blobUrl;
            
            // Update canvas size - this will trigger annotator re-initialization
            setCanvasSize({ width: canvasWidth, height: canvasHeight });
            
            resolve();
          };
          img.onerror = reject;
          img.src = blobUrl;
        });
        
        // Don't revoke URL immediately - let it stay for rendering
      } catch (error) {
        console.error('Failed to load image blob:', error);
        handleApiError(error, '画像の読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage, objectId, isOpen, imageReloadTrigger]);

  // Update tool
  const handleToolChange = (newTool) => {
    ensure().SetTool(newTool);
    setTool(newTool);
  };

  // Update color
  const handleColorChange = (newColor) => {
    ensure().SetStroke({ strokeStyle: newColor });
    setColor(newColor);
  };

  // Update line width
  const handleLineWidthChange = (newWidth) => {
    ensure().SetStroke({ lineWidth: newWidth });
    setLineWidth(newWidth);
  };

  // Update eraser size
  const handleEraserSizeChange = (newSize) => {
    ensure().SetEraser({ size: newSize });
    setEraserSize(newSize);
  };

  // Update fit mode
  const handleFitChange = (newFit) => {
    ensure().SetFit(newFit);
    setFit(newFit);
  };

  // Undo
  const handleUndo = () => {
    ensure().Undo();
  };

  // Redo
  const handleRedo = () => {
    ensure().Redo();
  };

  // Clear annotations
  const handleClear = () => {
    ensure().Clear();
  };

  // Save annotated image
  const handleSave = async () => {
    if (!selectedImage || !objectId || !annotRef.current) {
      handleApiError(new Error('画像が選択されていません'), '画像を選択してください');
      return;
    }

    setIsSaving(true);
    try {
      // Use objectId from props if not in image object
      const imageObjectId = selectedImage.objectId || objectId;
      
      let objectImageEditId;
      let objectImageId;

      if (saveOption === '1') {
        // Option 1: Keep same objectImageId, increment objectImageEditId
        const checkMaxObjectImageEditId = await mobileObjectDetailService.getMaxImageEditId(
          imageObjectId,
          selectedImage.objectImageId
        );
        objectImageEditId = checkMaxObjectImageEditId.objectImageEditId + 1;
        objectImageId = selectedImage.objectImageId;
      } else {
        // Option 2: Create new objectImageId with objectImageEditId as 0
        objectImageEditId = 0;
        // Don't set objectImageId - let backend generate a new one
        objectImageId = null;
      }

      // Save annotated image as blob
      const format = selectedImage.filenameExtension === 'png' ? 'png' : 
                     selectedImage.filenameExtension === 'jpg' || selectedImage.filenameExtension === 'jpeg' ? 'jpeg' : 'png';
      const annotatedBlob = await annotRef.current.Save({
        return: 'blob',
        format: format,
        quality: format === 'png' ? 1 : 0.92
      });

      // Convert blob to File
      const fileName = `${new Date().getTime()}.${selectedImage.filenameExtension || 'jpg'}`;
      const file = new File([annotatedBlob], fileName, { 
        type: format === 'png' ? 'image/png' : 'image/jpeg' 
      });

      // Create FormData for upload
      const formData = new FormData();
      formData.append('objectId', imageObjectId);
      if (objectImageId !== null) {
        formData.append('objectImageId', objectImageId);
      }
      formData.append('inputFile', file);
      formData.append('fileName', fileName);
      formData.append('fileSize', file.size);
      formData.append('imageFolderId', selectedImage.imageFolderId || 99);
      formData.append('objectImageEditId', objectImageEditId);

      // Upload the annotated image
      await mobileObjectDetailService.uploadEditedImage(formData);
      
      // Show success message
      showSuccessMessage('注釈画像の保存が完了しました。');
      
      // Refresh image list
      if (onHandwriteEditComplete) {
        onHandwriteEditComplete();
      }

    } catch (error) {
      console.error('Failed to save annotated image:', error);
      handleApiError(error, '注釈画像の保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    // Clean up
    setSelectedImage(null);
    setTool('pen');
    setColor('#000000');
    setLineWidth(4);
    setEraserSize(20);
    setFit('stretch');
    setSaveOption('1');
    setCanvasSize({ width: 300, height: 250 }); // Reset to small default（次回オープン時に getAvailableCanvasSize で上書き）
    setImageReloadTrigger(0); // Reset trigger
    pendingImageUrlRef.current = null;
    if (annotRef.current) {
      annotRef.current = null;
    }
    onClose();
  };

  const handleImageSelect = (image) => {
    // Since availableImages is already filtered, we can trust that the image is valid
    // But add a safety check anyway
    const isUploaded = image.isUploaded === 1 || image.isUploaded === '1' || (image.isUploaded && image.isUploaded !== 0);
    // objectImageId can be 0, so check for undefined/null/empty string specifically
    const hasObjectImageId = image.objectImageId !== undefined && 
                             image.objectImageId !== null && 
                             image.objectImageId !== '';
    
    if (!isUploaded || !hasObjectImageId) {
      console.warn('Attempted to select invalid image:', image);
      return;
    }
    
    // Clear any pending image URL when selecting an image
    pendingImageUrlRef.current = null;
    
    // Clear canvas if annotator is ready, but don't block selection if it's not
    if (annotRef.current) {
      try {
        annotRef.current.Clear();
      } catch (error) {
        console.warn('Error clearing canvas:', error);
        // Continue anyway
      }
    }
    
    // Reset canvas size and trigger image reload (even for same image)
    setCanvasSize(getAvailableCanvasSize(imageData));
    setImageReloadTrigger(prev => prev + 1); // Force reload
    setSelectedImage(image);
  };

  // Filter out images that are not uploaded or don't have objectImageId
  // Include images that are uploaded (isUploaded === 1) and have a valid objectImageId
  const availableImages = React.useMemo(() => {
    if (!imageData || imageData.length === 0) {
      return [];
    }
        
    const filtered = imageData.filter(img => {
      // Check if image is uploaded (can be 1, '1', or truthy number)
      const isUploaded = img.isUploaded === 1 || img.isUploaded === '1' || (img.isUploaded && img.isUploaded !== 0);
      // Check if objectImageId exists and is not empty
      const hasObjectImageId = img.objectImageId !== undefined && 
                               img.objectImageId !== null && 
                               img.objectImageId !== '';
      
      return isUploaded && hasObjectImageId;
    });
    
    return filtered;
  }, [imageData]);

  if (!isOpen) return null;

  return (
    <div style={styles.modal} data-testid="image-annotation-modal">
      <div style={styles.modalContent} data-testid="modal-content">
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>画像手書き編集</h3>
          <button
            onClick={handleClose}
            style={styles.closeButton}
            data-testid="close-modal-button"
          >
            閉じる
          </button>
        </div>

        <div style={styles.content}>
          {/* Image Selection Panel */}
          {availableImages.length > 0 && (
            <div style={styles.imageSelectionPanel} data-testid="image-selection-panel">
              <div style={styles.imageSelectionTitle}>画像選択</div>
              <div style={styles.imageSelectionGrid} data-testid="image-selection-grid">
                {availableImages.map((image, index) => {
                  const isSelected = selectedImage?.objectImageId === image.objectImageId;
                  return (
                    <div
                      key={image.objectImageId || index}
                      style={{
                        ...styles.imageSelectionItem,
                        ...(isSelected ? styles.imageSelectionItemSelected : {})
                      }}
                      onClick={() => handleImageSelect(image)}
                      data-testid={`image-selection-item-${image.objectImageId || index}`}
                    >
                      <img 
                        src={image.thumbnailUrl || image.mainImageUrl || '/core/img/errorimage.png'} 
                        alt={`画像 ${index + 1}`}
                        style={styles.imageSelectionThumbnail}
                        onError={(e) => {
                          e.target.src = '/core/img/errorimage.png';
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Canvas Area */}
          <div ref={canvasContainerRef} style={styles.canvasContainer} data-testid="canvas-container">
            {/* Wrapper div for CanvasAnnotator - library expects canvas parent to be position: relative */}
            {/* This wrapper is centered by the parent's flexbox, and the library positions canvases absolutely within it */}
            <div
              style={{
                ...styles.canvasWrapper,
                width: `${canvasSize.width}px`,
                height: `${canvasSize.height}px`
              }}
            >
              <canvas 
                ref={baseCanvasRef}
                width={canvasSize.width}
                height={canvasSize.height}
                style={styles.canvas}
                data-testid="annotation-canvas"
              />
            </div>
            {isLoading && (
              <div style={styles.loadingOverlay}>
                <div style={styles.loadingText}>読み込み中...</div>
              </div>
            )}
            {!selectedImage && !isLoading && (
              <div style={styles.noImageMessage}>
                <div style={styles.noImageText}>画像を選択してください</div>
              </div>
            )}
          </div>

          {/* Controls Panel */}
          <div style={styles.controlsPanel} data-testid="controls-panel">
            <div style={styles.controlsRow}>
              {/* Tool Selection */}
              <div style={styles.controlGroup} data-testid="tool-selection">
                <div style={styles.controlLabel}>ツール</div>
                <div style={styles.toolButtons}>
                  <button
                    onClick={() => handleToolChange('pen')}
                    style={{
                      ...styles.toolButton,
                      ...(tool === 'pen' ? styles.toolButtonActive : {})
                    }}
                    data-testid="pen-tool-button"
                  >
                    ペン
                  </button>
                  <button
                    onClick={() => handleToolChange('eraser')}
                    style={{
                      ...styles.toolButton,
                      ...(tool === 'eraser' ? styles.toolButtonActive : {})
                    }}
                    data-testid="eraser-tool-button"
                  >
                    消しゴム
                  </button>
                </div>
              </div>

              {/* Color Picker */}
              {tool === 'pen' && (
                <div style={styles.controlGroup} data-testid="color-selection">
                  <div style={styles.controlLabel}>色</div>
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => handleColorChange(e.target.value)}
                    style={styles.colorPicker}
                    data-testid="color-picker"
                  />
                </div>
              )}

              {/* Line Width */}
              {tool === 'pen' && (
                <div style={styles.controlGroup} data-testid="line-width-selection">
                  <div style={styles.controlLabel}>太さ: {lineWidth}px</div>
                  <input
                    type="range"
                    min={1}
                    max={40}
                    value={lineWidth}
                    onChange={(e) => handleLineWidthChange(Number(e.target.value))}
                    style={styles.slider}
                    data-testid="line-width-slider"
                  />
                </div>
              )}

              {/* Eraser Size */}
              {tool === 'eraser' && (
                <div style={styles.controlGroup} data-testid="eraser-size-selection">
                  <div style={styles.controlLabel}>消し幅: {eraserSize}px</div>
                  <input
                    type="range"
                    min={5}
                    max={80}
                    value={eraserSize}
                    onChange={(e) => handleEraserSizeChange(Number(e.target.value))}
                    style={styles.slider}
                    data-testid="eraser-size-slider"
                  />
                </div>
              )}

              {/* Fit Mode */}
              <div style={styles.controlGroup} data-testid="fit-mode-selection">
                <div style={styles.controlLabel}>表示モード</div>
                <select
                  value={fit}
                  onChange={(e) => handleFitChange(e.target.value)}
                  style={styles.select}
                  data-testid="fit-mode-select"
                >
                  <option value="contain">contain（全体）</option>
                  <option value="cover">cover（全面）</option>
                  <option value="stretch">stretch（引き伸ばし）</option>
                </select>
              </div>

              {/* Undo/Redo/Clear */}
              <div style={styles.controlGroup} data-testid="action-buttons">
                <div style={styles.controlLabel}>操作</div>
                <div style={styles.actionButtons}>
                  <button
                    onClick={handleUndo}
                    style={styles.actionButton}
                    data-testid="undo-button"
                    title="Undo"
                  >
                    元に戻す
                  </button>
                  <button
                    onClick={handleRedo}
                    style={styles.actionButton}
                    data-testid="redo-button"
                    title="Redo"
                  >
                    やり直す
                  </button>
                  <button
                    onClick={handleClear}
                    style={{...styles.actionButton, ...styles.clearButton}}
                    data-testid="clear-button"
                    title="Clear"
                  >
                    クリア
                  </button>
                </div>
              </div>
            </div>

            {/* Save Option */}
            <div style={{ marginTop: '8px' }} data-testid="save-option-selection">
              <div style={styles.controlLabel}>保存オプション</div>
              <select
                value={saveOption}
                onChange={(e) => setSaveOption(e.target.value)}
                style={styles.select}
                data-testid="save-option-select"
              >
                <option value="1">元画像を上書きして保存</option>
                <option value="2">新しい画像として保存</option>
              </select>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={isSaving || !selectedImage || isLoading}
              style={{
                ...styles.saveButton,
                ...((isSaving || !selectedImage || isLoading) ? styles.saveButtonDisabled : {})
              }}
              data-testid="save-button"
            >
              {isSaving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>

      {/* Message Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title={
          messageType === 'error' ? 'エラー' :
          messageType === 'success' ? '成功' :
          messageType === 'warning' ? '警告' :
          '情報'
        }
        message={errorMessage}
        type={messageType}
        buttonText="閉じる"
        data-testid="message-dialog"
      />
    </div>
  );
};

export default ImageHandwriteEditModal;

